import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * Comunicados oficiais, lidos e gravados pela API
 * (`apps/api/src/modulos/comunicacao`, rotas `bff/v1/admin/comunicados`).
 * O contrato é o do banco (`comunicados_oficiais`).
 *
 * Só a administração usa. A confirmação de leitura pelo público-alvo não existe
 * ainda: depende de regra de segmentação (quem vê qual comunicado) que não está
 * decidida, e por isso este arquivo não tem "marcar como lido" nem "pendentes".
 */

export const PUBLICOS_ALVO = ['CREDENCIADO', 'OPERADOR_DTP', 'OPERADOR_SME', 'TODOS'] as const;
export type PublicoAlvo = (typeof PUBLICOS_ALVO)[number];

export const ROTULO_DO_PUBLICO: Record<PublicoAlvo, string> = {
  CREDENCIADO: 'Credenciados',
  OPERADOR_DTP: 'Operadores DTP',
  OPERADOR_SME: 'Operadores SME/DRE',
  TODOS: 'Todos',
};

export type SituacaoDoComunicado = 'RASCUNHO' | 'PUBLICADO' | 'EXPIRADO' | 'CANCELADO';

export interface ComunicadoOficial {
  id: string;
  titulo: string;
  resumo: string | null;
  publicoAlvo: PublicoAlvo;
  /** Datas sem hora, `aaaa-mm-dd`. */
  vigenciaInicio: string;
  vigenciaFim: string | null;
  /** PUBLICADO com a vigência vencida chega como EXPIRADO. */
  status: SituacaoDoComunicado;
  exigeConfirmacao: boolean;
  publicadoEm: string | null;
  criadoEm: string;
}

export interface ListaDeComunicados {
  itens: ComunicadoOficial[];
  total: number;
  pagina: number;
  tamanho: number;
}

export interface NovoComunicado {
  titulo: string;
  resumo: string | null;
  conteudoMarkdown: string;
  publicoAlvo: PublicoAlvo;
  vigenciaInicio: string;
  vigenciaFim: string | null;
  exigeConfirmacao: boolean;
  /** Repetir a chamada com a mesma chave devolve o mesmo comunicado. */
  chaveIdempotencia: string;
}

export const CAMINHO_DOS_COMUNICADOS = '/bff/v1/admin/comunicados';

export interface FiltroDeComunicados {
  status?: SituacaoDoComunicado;
  pagina: number;
  tamanho: number;
}

export const CHAVES = {
  lista: (filtro: FiltroDeComunicados) => ['admin', 'comunicados', 'lista', filtro] as const,
  tudo: ['admin', 'comunicados'] as const,
};

export function useComunicados(filtro: FiltroDeComunicados) {
  return useQuery({
    queryKey: CHAVES.lista(filtro),
    queryFn: ({ signal }) => {
      const consulta = new URLSearchParams({
        pagina: String(filtro.pagina),
        tamanho: String(filtro.tamanho),
      });
      if (filtro.status) consulta.set('status', filtro.status);
      return chamarApi<ListaDeComunicados>(`${CAMINHO_DOS_COMUNICADOS}?${consulta.toString()}`, {
        sinal: signal,
      });
    },
    placeholderData: keepPreviousData,
    retry: naoInsistirEmRecusa,
  });
}

export function useCriarComunicado() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (novo: NovoComunicado) =>
      chamarApi<ComunicadoOficial>(CAMINHO_DOS_COMUNICADOS, { metodo: 'POST', corpo: novo }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVES.tudo }),
  });
}

export function usePublicarComunicado() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      chamarApi<ComunicadoOficial>(
        `${CAMINHO_DOS_COMUNICADOS}/${encodeURIComponent(id)}/publicar`,
        { metodo: 'POST' },
      ),
    // Recusa por "já publicado" também deixa a lista desatualizada: recarrega nos dois casos.
    onSettled: () => cliente.invalidateQueries({ queryKey: CHAVES.tudo }),
  });
}

/** Mensagem da API quando ela disse o que fazer; senão, a genérica. */
export function mensagemDoErro(erro: unknown, generica: string): string {
  return erro instanceof ErroApi && erro.message ? erro.message : generica;
}
